import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { select, Store } from '@ngrx/store';
// Misc
import { some as _some } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { first, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { env } from '@amfam/environment';
import { PolicySelectors } from '@amfam/policy/data-access';
import { RiskModel, VehicleType } from '@amfam/policy/models';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { DockingBarService, PageTitleService } from '@amfam/ui-kit';

import * as fromRoot from '../../../core/store';
import { SendCoverageAnalyticsAction } from '../../+state/coverage-change.actions';

@Component({
  selector: 'ds-coverage-change-select-risk',
  templateUrl: './coverage-change-select-risk.component.html',
  styleUrls: ['./coverage-change-select-risk.component.scss'],
  standalone: false
})
export class CoverageChangeSelectRiskComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() isPartner: boolean;
  loading: Observable<boolean>;

  vehicles: RiskModel[];
  riskSelectionForm: UntypedFormGroup;
  preSelectedRisk: RiskModel;
  currentVehicle: RiskModel;
  changeCoverageHeaderText: string;
  private vehicleId: string;
  isModelCoverageEnabled: boolean;
  isPartner$: Observable<boolean> = this.store.select(BrandSelectors.selectIsPartner);

  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private store: Store,
    private formBuilder: UntypedFormBuilder,
    private featureService: FeatureFlagService,
    private pageTitle: PageTitleService,
    private dockingBarService: DockingBarService,
    private route: ActivatedRoute,
    private copyService: CopyService
  ) {}

  ngOnInit() {
    this.changeCoverageHeaderText = this.copyService.getCopy(
      'policy.coverageChangeSelectRiskHeader'
    );
    const title = this.copyService.getCopy('policy.changeCoverageTitle');
    this.pageTitle.set(title);
    this.dockingBarService.registerHeading(title);

    // List the risks
    this.loading = this.store.select(PolicySelectors.selectPoliciesLoading);

    // Get the risk id
    this.route.params.pipe(take(1)).subscribe(params => {
      this.vehicleId = params['id'];
    });

    this.store
      .pipe(
        select(
          fromRoot.getCoverageChangeRisks,
          this.featureService.isEnabled('kyd_model_coverage') ? [] : env.modelCoverageDisabledStates
        ),
        takeUntil(this.stop$)
      )
      .subscribe(data => {
        this.vehicles = data?.filter(risk => risk.vehicleType === VehicleType.Auto);
        // Set up the preselected risk if there's a url param that matchs a risk id
        if (_some(this.vehicles, { id: this.vehicleId })) {
          this.preSelectedRisk = this.vehicles.find(risk => risk.id === this.vehicleId);
          this.currentVehicle = this.setCurrentRiskId(this.preSelectedRisk);
        }
      });

    // Initialize the form
    this.riskSelectionForm = this.buildRiskSelectionForm(this.formBuilder);
  }

  ngAfterViewInit() {
    this.store.dispatch(
      new SendCoverageAnalyticsAction({
        pageAnalyticName: 'SelectPolicy'
      })
    );
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  riskSelectionSubmit() {
    this.store
      .select(BrandSelectors.selectIsPartner)
      .pipe(withLatestFrom(this.store.select(PolicySelectors.selectAdvanceAutoPolicies)), first())
      .subscribe(([isPartner, policies]) => {
        const policy = policies.find(p => p.policyNumber === this.currentVehicle.policyNumber);
        const vehicle = policy.vehicles.find(v => v.vehicleId === this.currentVehicle.vehicleId);
        this.store.dispatch(
          fromRouterActions.Go({
            path: ['/policies/change-coverage/' + vehicle.vehicleId + '/get-quote']
          })
        );
      });
  }

  cancel() {
    // Route to history -1 ??
    this.store.dispatch(
      fromRouterActions.Go({
        path: ['/policies']
      })
    );
  }

  setCurrentRiskId(riskId) {
    return this.vehicles.find(risk => risk.id === riskId);
  }

  changeRisk(riskId) {
    this.riskSelectionForm.markAsDirty();
    this.currentVehicle = this.setCurrentRiskId(riskId);
  }

  /**
   * @author: Jesse Clark
   * @param void
   * @returns FormGroup : Return the form which has the new controls and the initialized values.
   */
  private buildRiskSelectionForm(fb: UntypedFormBuilder): UntypedFormGroup {
    return fb.group({});
  }
}
